import { useState, useEffect } from 'react';

interface AuthToastProps {
  isVisible: boolean;
  isAuthenticating: boolean;
  isSuccess?: boolean;
  isWalletConnected?: boolean;
  isWalletLoading?: boolean;
  onSignIn: () => void;
  onDismiss: () => void;
}

export function AuthToast({
  isVisible,
  isAuthenticating,
  isSuccess = false,
  isWalletConnected = false,
  isWalletLoading = false,
  onSignIn,
  onDismiss,
}: AuthToastProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Small delay to trigger animation
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
    }
  }, [isVisible]);

  if (!isVisible && !isAnimating) return null;

  return (
    <div
      className={`
        fixed bottom-0 left-0 right-0 z-50
        transform transition-transform duration-300 ease-out
        ${isAnimating && isVisible ? 'translate-y-0' : 'translate-y-full'}
      `}
    >
      <div className={`${isSuccess ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'} text-white shadow-lg transition-colors duration-300`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {isSuccess ? (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                )}
              </div>
              <div>
                {isSuccess ? (
                  <p className="font-medium">Signed in successfully!</p>
                ) : !isWalletConnected ? (
                  <>
                    <p className="font-medium">Connect wallet to favorite NFTs</p>
                    <p className="text-sm text-blue-100">
                      Connect your Tezos wallet to save your favorites
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">Sign in to favorite NFTs</p>
                    <p className="text-sm text-blue-100">
                      Sign a message to verify your wallet
                    </p>
                  </>
                )}
              </div>
            </div>

            {!isSuccess && (
              <div className="flex items-center gap-3">
                <button
                  onClick={onSignIn}
                  disabled={isAuthenticating || isWalletLoading}
                  className={`
                    px-6 py-2 rounded-lg font-medium transition-all
                    ${isAuthenticating || isWalletLoading
                      ? 'bg-white/20 cursor-wait'
                      : 'bg-white text-blue-600 hover:bg-blue-50 active:scale-95'
                    }
                  `}
                >
                  {isWalletLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Connecting...
                    </span>
                  ) : isAuthenticating ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Check wallet to sign
                    </span>
                  ) : !isWalletConnected ? (
                    'Connect Wallet'
                  ) : (
                    'Sign In'
                  )}
                </button>

                <button
                  onClick={onDismiss}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="Dismiss"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
